/* import __COLOCATED_TEMPLATE__ from './space.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { states, objectTypes } from 'embercom/models/data/matching-system/matching-constants';
import { task as trackedTask, type TaskInstance } from 'ember-resources/util/ember-concurrency';
import { type TaskGenerator } from 'ember-concurrency';
import {
  type ContentSearchParams,
  type NormalizedContentServiceSearchResponse,
} from 'embercom/lib/content-service/search-api';
import type HelpCenterSite from 'embercom/models/help-center-site';
import type { PaletteColor } from '@intercom/pulse/template-registry';
import type SpaceModel from 'embercom/models/messenger-settings/space';

export type Args = {
  space: SpaceModel;
  roundTopCorners: boolean;
  roundBottomCorners: boolean;
  onToggle: () => void;
  sortableHandlerClass: string;
};

export default class Space extends Component<Args> {
  @service declare helpCenterService: { site: HelpCenterSite };
  @service declare outboundHomeService: {
    contentSearch: (params: ContentSearchParams) => Promise<NormalizedContentServiceSearchResponse>;
  };
  @service declare appService: { app: { id: string } };

  checklistSearchResult: TaskInstance<NormalizedContentServiceSearchResponse> = trackedTask(
    this,
    taskFor(this.searchForLiveChecklists),
    () => [],
  );

  get draggableHandleColor(): PaletteColor {
    if (this.args.space.isHub) {
      return 'text-disabled';
    } else {
      return 'text-default';
    }
  }

  get isHelpCenterEnabled() {
    return this.helpCenterService.site?.turnedOn;
  }

  get liveChecklistCount() {
    return this.checklistSearchResult.value?.contentWrappers.length ?? 0;
  }

  get hasLiveChecklists(): boolean {
    return Boolean(this.liveChecklistCount && this.liveChecklistCount > 0);
  }

  get isDisabledForHelp() {
    return this.args.space.isHelp && !this.isHelpCenterEnabled;
  }

  get isDisabledForChecklists() {
    // Only disable the toggle when the space is currently enabled and set live
    // If they're just flipping the toggle without saving changes, then we don't want to disable it yet.
    return (
      this.args.space.isTasks &&
      this.args.space.currentlyEnabledAndSetLive &&
      (this.hasLiveChecklists || this.checklistSearchResult.isRunning)
    );
  }

  get isDisabled() {
    return this.isDisabledForHelp || this.isDisabledForChecklists;
  }

  @dropTask
  *searchForLiveChecklists(): TaskGenerator<NormalizedContentServiceSearchResponse> {
    return yield this.outboundHomeService.contentSearch({
      app_id: this.appService.app.id,
      object_types: [objectTypes.checklist],
      states: [states.live],
      per_page: 5,
    });
  }

  get isSearchingForLiveChecklists() {
    return taskFor(this.searchForLiveChecklists).isRunning;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'MessengerSettings::Layout::Space': typeof Space;
    'messenger-settings/layout/space': typeof Space;
  }
}
